import { EmptyState } from '@/components/ui/EmptyState';
import { NavBar } from '@/components/ui/NavBar';
import { Screen } from '@/components/ui/Screen';
import { useHasSynced } from '@/lib/sync-status';
import { useDelayedTrue } from '@/lib/use-delayed-true';

export interface NotFoundScreenProps {
  /** Back action for the NavBar chevron and the "Go back" button. */
  onBack: () => void;
  /** Centered NavBar title shown alongside the not-found state. */
  navTitle?: string;
  /** Not-found headline. */
  title?: string;
  /** Supporting line under the headline. */
  body?: string;
}

/**
 * Full-screen fallback for a detail route whose record resolved to `null`.
 *
 * That happens both during the brief local PowerSync load and permanently when
 * the row doesn't exist or the user has no access — so the not-found copy is
 * gated behind `useDelayedTrue(useHasSynced())`: while sync hasn't settled we
 * render only the bare NavBar (the normal load window), and a record that
 * resolves in that window never flashes the empty state. Once PowerSync reports
 * a completed sync that holds briefly, a still-null record is genuinely missing.
 */
export function NotFoundScreen({ onBack, navTitle, title, body }: NotFoundScreenProps) {
  const settled = useDelayedTrue(useHasSynced(), 300);

  return (
    <Screen edges={['top']}>
      <NavBar onBack={onBack} title={settled ? navTitle : undefined} />
      {settled ? (
        <EmptyState
          className="flex-1 justify-center"
          icon="🔍"
          title={title ?? 'Not found'}
          body={body}
          action={{ label: 'Go back', onPress: onBack, variant: 'outline' }}
        />
      ) : null}
    </Screen>
  );
}
